(() => {
  if (customElements.get('habibi-product')) return;
  class HabibiProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.form = this.querySelector('.hbp-form');
      this.quantity = this.form?.querySelector('[name="quantity"]');
      this.addButton = this.querySelector('[data-add]');
      this.stickyButton = this.querySelector('[data-sticky-add]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.index = Math.max(0, this.slides.findIndex(slide => !slide.hidden));
      this.dialog = this.querySelector('dialog');
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.variant = this.variants.variants.find(variant => variant.id === this.variants.selected);
      this.on(this, 'click', event => this.handleClick(event));
      this.on(this.form, 'submit', event => this.addToCart(event));
      this.on(this.querySelector('[data-variant-select]'), 'change', event => this.changeVariant(event.target.value));
      this.on(this.quantity, 'change', () => this.normalizeQuantity());
      const stage = this.querySelector('.hbp-gallery__stage');
      this.on(stage, 'keydown', event => {
        if (event.target !== stage) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showSlide(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.on(stage, 'touchstart', event => {
        if (event.target.closest('button, video, iframe, model-viewer')) return;
        this.touch = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }, { passive: true });
      this.on(stage, 'touchend', event => {
        if (!this.touch) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.showSlide(this.index + (dx < 0 ? 1 : -1));
        this.touch = null;
      }, { passive: true });
      this.on(this.dialog, 'keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.nextZoom(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      this.on(this.dialog, 'close', () => { document.documentElement.style.overflow = this.previousOverflow || ''; });
      this.on(window, 'popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        this.changeVariant(id || this.variants.selected, false);
      });
      this.observer = new IntersectionObserver(([entry]) => {
        this.querySelector('[data-sticky]').hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }, { threshold: 0 });
      this.observer.observe(this.form);
      this.showSlide(this.index);
      // A direct variant URL takes precedence over the editorial opening image.
      if (new URL(location.href).searchParams.has('variant') && this.variant?.mediaId) this.selectVariantMedia();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.initialized = false;
    }
    on(target, type, callback, options = {}) {
      target?.addEventListener(type, callback, { ...options, signal: this.abort.signal });
    }
    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-thumb')) this.showSlide(this.thumbs.indexOf(button));
      if (button.hasAttribute('data-gallery-prev')) this.showSlide(this.index - 1);
      if (button.hasAttribute('data-gallery-next')) this.showSlide(this.index + 1);
      if (button.hasAttribute('data-zoom-open')) this.openZoom();
      if (button.hasAttribute('data-zoom-close')) this.dialog.close();
      if (button.hasAttribute('data-zoom-prev')) this.nextZoom(-1);
      if (button.hasAttribute('data-zoom-next')) this.nextZoom(1);
      if (button.hasAttribute('data-qty-minus') || button.hasAttribute('data-qty-plus')) {
        this.normalizeQuantity();
        if (button.hasAttribute('data-qty-plus')) this.quantity.stepUp();
        else this.quantity.stepDown();
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    normalizeQuantity() {
      const min = Number(this.quantity.min) || 1;
      const step = Number(this.quantity.step) || 1;
      const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
      const value = Number(this.quantity.value);
      const aligned = min + Math.round((Math.max(min, Number.isFinite(value) ? value : min) - min) / step) * step;
      const lastValid = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : max;
      this.quantity.value = Math.max(min, Math.min(lastValid, aligned));
    }
    showSlide(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== this.index;
        slide.classList.toggle('is-active', i === this.index);
        if (i !== this.index) {
          slide.querySelectorAll('video').forEach(video => video.pause());
          slide.querySelector('model-viewer')?.pause?.();
          // Reload an active external player when hidden, so its sound stops.
          const iframe = slide.querySelector('iframe');
          if (iframe && slide.dataset.wasActive === 'true') iframe.src = iframe.src;
        }
        slide.dataset.wasActive = String(i === this.index);
      });
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === this.index)));
      const activeThumb = this.thumbs[this.index];
      if (activeThumb) {
        const rail = activeThumb.parentElement;
        rail.scrollLeft = activeThumb.offsetLeft - rail.offsetLeft - rail.clientWidth / 2 + activeThumb.clientWidth / 2;
      }
      const slide = this.slides[this.index];
      const count = this.querySelector('[data-gallery-count]');
      if (count) count.textContent = `${this.index + 1} / ${this.slides.length}`;
      this.querySelector('[data-gallery-caption]').textContent = slide.dataset.caption || '';
      this.querySelector('[data-zoom-open]').hidden = !slide.dataset.zoom;
      if (this.dialog.open) this.updateZoom();
    }
    openZoom() {
      if (!this.slides[this.index]?.dataset.zoom) return;
      this.updateZoom();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
    }
    updateZoom() {
      const slide = this.slides[this.index];
      const image = this.querySelector('[data-zoom-image]');
      image.src = slide.dataset.zoom;
      image.alt = slide.dataset.caption || 'Parfum HABIBI';
      this.querySelector('[data-zoom-caption]').textContent = slide.dataset.caption || '';
    }
    nextZoom(direction) {
      for (let step = 1; step <= this.slides.length; step++) {
        const index = (this.index + direction * step + this.slides.length) % this.slides.length;
        if (this.slides[index].dataset.zoom) { this.showSlide(index); break; }
      }
    }
    selectVariantMedia() {
      const index = this.slides.findIndex(slide => slide.dataset.mediaId === String(this.variant.mediaId));
      if (index >= 0) this.showSlide(index);
    }
    changeVariant(id, updateUrl = true) {
      const variant = this.variants.variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      this.variant = variant;
      this.form.querySelector('[name="id"]').value = variant.id;
      this.querySelectorAll('[data-price]').forEach(item => { item.innerHTML = variant.price; });
      const compare = this.querySelector('[data-compare]');
      compare.innerHTML = variant.compare;
      compare.hidden = !variant.onSale;
      const saving = this.querySelector('[data-saving]');
      saving.innerHTML = `Economisești ${variant.saving}`;
      saving.hidden = !variant.onSale;
      this.querySelector('[data-stock]').dataset.available = String(variant.available);
      this.querySelector('[data-stock-text]').textContent = variant.available ? 'Disponibil pentru comandă' : 'Momentan indisponibil';
      this.quantity.min = variant.min;
      this.quantity.step = variant.step;
      if (variant.max != null) this.quantity.max = variant.max;
      else this.quantity.removeAttribute('max');
      this.normalizeQuantity();
      this.setBusy(this.busy || false);
      this.querySelector('[data-added]').hidden = true;
      this.querySelector('[data-error]').hidden = true;
      if (variant.mediaId) this.selectVariantMedia();
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }
    setBusy(busy) {
      this.busy = busy;
      [this.addButton, this.stickyButton].forEach(button => { button.disabled = busy || !this.variant.available; });
      this.querySelectorAll('[data-add-label]').forEach(label => {
        label.textContent = busy ? 'Se adaugă…' : this.variant.available ? 'Adaugă în coș' : 'Stoc epuizat';
      });
      this.form.setAttribute('aria-busy', String(busy));
      const select = this.querySelector('[data-variant-select]');
      if (select) select.disabled = busy;
    }
    async addToCart(event) {
      if (!window.fetch || !this.variant) return;
      event.preventDefault();
      if (this.busy || !this.variant.available) return;
      this.normalizeQuantity();
      if (!this.form.reportValidity()) return;
      const payload = new FormData(this.form);
      const quantity = this.quantity.value;
      const error = this.querySelector('[data-error]');
      const added = this.querySelector('[data-added]');
      error.hidden = true;
      added.hidden = true;
      this.setBusy(true);
      try {
        const response = await fetch(this.dataset.cartAdd, { method: 'POST', body: payload, headers: { Accept: 'application/json' } });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Produsul nu a putut fi adăugat. Verifică disponibilitatea și cantitatea.');
        this.querySelector('[data-added-message]').textContent = `${quantity} × ${data.product_title || data.title} — adăugat în coș.`;
        added.hidden = false;
        // Expose confirmation even when adding through the fixed bar far down the page.
        if (this.form.getBoundingClientRect().bottom < 0) {
          added.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
        }
        this.dispatchEvent(new CustomEvent('habibi:cart-added', { bubbles: true, detail: data }));
        // A count refresh failure must never turn a successful add into an error.
        this.refreshCartCount();
      } catch (failure) {
        error.textContent = failure instanceof TypeError ? 'Conexiunea s-a întrerupt. Verifică mai întâi coșul înainte de a încerca din nou.' : failure.message;
        error.hidden = false;
        error.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } finally {
        this.setBusy(false);
      }
    }
    async refreshCartCount() {
      try {
        const response = await fetch(this.dataset.cartJson, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        const link = document.querySelector('.hbh-cart');
        if (!link) return;
        let badge = link.querySelector('b');
        if (!badge) { badge = document.createElement('b'); link.append(badge); }
        badge.textContent = cart.item_count;
        link.setAttribute('aria-label', `Coș, ${cart.item_count} produse`);
      } catch (_) { /* The product was already added successfully. */ }
    }
  }
  customElements.define('habibi-product', HabibiProduct);
})();
